<template>
  <div class="inline-block">
    <div
      v-if="has_custom_trigger_slot"
      :data-test="data_test"
      @click.prevent="(event) => togglePopover(event)"
    >
      <slot name="custom_trigger" />
    </div>

    <div
      v-else-if="show_ellipsis_trigger"
      class="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
      :data-test="data_test"
      @click.prevent="(event) => togglePopover(event)"
    >
      <FontAwesomeIcon
        class="flex-center text-gray-500"
        :icon="['far', ellipsis_icon]"
        size="lg"
      />
    </div>

    <component
      :is="button_component"
      v-else
      :action="action"
      :data-test="data_test"
      :hide_text="hide_text"
      v-bind="button_props"
      @click.stop="togglePopover"
    >
      <div
        v-if="icon || text || has_trigger_slot"
        class="flex items-center gap-2"
      >
        <FontAwesomeIcon
          v-if="icon"
          :icon="icon"
        />
        <span v-if="text">{{ text }}</span>
        <slot name="trigger" />
      </div>
    </component>

    <Popover
      ref="popover"
      class="dropdown-popover z-2000!"
      :pt="{
        root: '!border !border-neutral-200',
        content: '!p-1 !m-0 !max-h-[50vh] !overflow-y-auto !overscroll-contain',
      }"
    >
      <div class="flex min-w-[200px] flex-col gap-0.5">
        <slot />
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { computed, provide, ref, useSlots } from 'vue';
import INJECT_KEYS from '@/app/data/inject_keys';

const slots = useSlots();

const props = defineProps({
  icon: { type: [String, Array], default: null },
  text: { type: String, default: null },
  data_test: { type: String, default: 'button-dropdown' },
  variant: {
    type: String,
    default: 'secondary',
    validator: (v) => ['secondary', 'tertiary', 'icon'].includes(v),
  },
  action: { type: String, default: null },
  hide_text: { type: Boolean },
  button_props: { type: Object, default: () => ({}) },
  ellipsis_vertical: { type: Boolean },
});

const popover = ref(null);

const has_trigger_slot = computed(() => !!slots.trigger);
const has_custom_trigger_slot = computed(() => !!slots.custom_trigger);

const ellipsis_icon = computed(() => (props.ellipsis_vertical ? 'ellipsis-vertical' : 'ellipsis'));

const show_ellipsis_trigger = computed(() => {
  if (has_trigger_slot.value) return false;
  if (has_custom_trigger_slot.value) return false;
  if (props.variant === 'icon') return true;
  if (props.action) return false;
  return !props.icon && !props.text;
});

const button_component = computed(() => {
  if (props.variant === 'tertiary' || props.variant === 'icon') return 'ButtonTertiary';
  return 'ButtonSecondary';
});

const togglePopover = (event) => {
  popover.value.toggle(event);
};

const hide = () => {
  popover.value.hide();
};

provide(INJECT_KEYS.DROPDOWN_HIDE, hide);

defineExpose({ togglePopover, hide });
</script>
